import type { CSSProperties } from 'react';
import { JalaliDateInput } from './JalaliDateInput.js';
import { TimeOfDayInput } from './TimeOfDayInput.js';

export interface JalaliDateTimeInputProps {
  /** `''` or `YYYY-MM-DDTHH:mm` — the same shape a native `datetime-local` input produces, so this is a drop-in replacement on the wire. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  style?: CSSProperties;
}

function splitValue(value: string): { date: string; time: string } {
  if (!value) return { date: '', time: '' };
  const [date, time] = value.split('T');
  return { date: date ?? '', time: time ?? '' };
}

/**
 * Date + time-of-day pair, date rendered Jalali (`JalaliDateInput`), time
 * via `TimeOfDayInput` — clock time has no calendar system, so there's
 * nothing to convert there, only the date needs the Jalali/Shamsi UX (task
 * §3). `TimeOfDayInput` is a custom Persian-styled trigger, not a native
 * `<input type="time">`: the native control renders its own
 * untranslatable `--:--` placeholder glyph with no way to swap in Persian
 * text (QA finding #8). Combines back into the same `YYYY-MM-DDTHH:mm`
 * shape a native `datetime-local` input produces, so callers built around
 * that string need no other change.
 */
export function JalaliDateTimeInput({
  value,
  onChange,
  disabled,
  style,
}: JalaliDateTimeInputProps) {
  const { date, time } = splitValue(value);

  function setDate(nextDate: string) {
    if (!nextDate) {
      onChange('');
      return;
    }
    onChange(`${nextDate}T${time || '00:00'}`);
  }

  function setTime(nextTime: string) {
    if (!date) return; // time alone isn't a usable value — pick a date first
    onChange(`${date}T${nextTime || '00:00'}`);
  }

  return (
    <div style={{ display: 'flex', gap: 8, ...style }}>
      <JalaliDateInput value={date} onChange={setDate} disabled={disabled} style={{ flex: 1 }} />
      <TimeOfDayInput
        value={time}
        onChange={setTime}
        disabled={disabled || !date}
        style={{ width: 130 }}
      />
    </div>
  );
}
